import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { sessionKey } from '@/app/query-client'
import { http } from '@/lib/api/client'
import { isApiError } from '@/lib/api/errors'
import type { InvitationPreview, Resource, Session, Workspace } from '@/lib/api/types'
import type { SignUpSource } from '@/lib/campaign'

async function fetchSession(): Promise<Session | null> {
  try {
    return (await http.get<Resource<Session>>('/me')).data
  } catch (error) {
    if (isApiError(error) && error.isUnauthenticated) return null
    throw error
  }
}

export function useSession() {
  return useQuery({ queryKey: sessionKey, queryFn: fetchSession, staleTime: 60_000 })
}

/**
 * The signed-in session; only call inside routes guarded by <RequireAuth>. When the session
 * ends (signing out, or it expired), the last one is kept for the moment before <RequireAuth>
 * sends the browser to sign-in, so screens still on the page don't fail while they go.
 */
export function useAuthedSession(): Session {
  const { data } = useSession()
  const [last, setLast] = useState(data)
  if (data && data !== last) setLast(data)
  const session = data ?? last
  if (!session) throw new Error('useAuthedSession used outside an authenticated route')
  return session
}

function useSessionMutation<TInput>(path: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: TInput) => (await http.post<Resource<Session>>(path, input)).data,
    onSuccess: (session) => {
      queryClient.setQueryData(sessionKey, session)
    },
  })
}

export interface LoginInput {
  email: string
  password: string
  remember: boolean
}

/** Signing in answers with the session, or, with two-step verification on, asks for the code first. */
export type LoginResult = Session | { two_factor: 'challenge' }

export const needsTwoFactor = (result: LoginResult): result is { two_factor: 'challenge' } =>
  'two_factor' in result

export function useLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: LoginInput) =>
      (await http.post<Resource<LoginResult>>('/auth/login', input)).data,
    onSuccess: (result) => {
      if (!needsTwoFactor(result)) queryClient.setQueryData(sessionKey, result)
    },
  })
}

/** The second step of signing in: a code from the authenticator app, or a recovery code. */
export const useTwoFactorSignIn = () =>
  useSessionMutation<{ code?: string; recovery_code?: string }>('/auth/two-factor')

/** Two-step verification in Your account. Each answer that changes it carries the new session. */
type WithRecoveryCodes = Session & { recovery_codes: string[] }

export function useStartTwoFactor() {
  return useMutation({
    mutationFn: async () =>
      (await http.post<Resource<{ secret: string; otpauth_url: string }>>('/me/two-factor')).data,
  })
}

function useTwoFactorChange<TInput, TResult extends Session>(path: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: TInput) => (await http.post<Resource<TResult>>(path, input)).data,
    onSuccess: (result) => {
      const { user, workspaces, current_workspace_id } = result
      queryClient.setQueryData(sessionKey, { user, workspaces, current_workspace_id })
    },
  })
}

export const useConfirmTwoFactor = () =>
  useTwoFactorChange<{ code: string }, WithRecoveryCodes>('/me/two-factor/confirm')
export const useRenewRecoveryCodes = () =>
  useTwoFactorChange<{ code: string }, WithRecoveryCodes>('/me/two-factor/recovery-codes')
export const useDisableTwoFactor = () =>
  useTwoFactorChange<{ code?: string; recovery_code?: string }, Session>('/me/two-factor/disable')

/** Close a one-time in-app notice for good. */
export function useDismissNotice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (notice: string) =>
      (await http.post<Resource<Session>>(`/me/notices/${notice}/dismiss`)).data,
    onSuccess: (session) => queryClient.setQueryData(sessionKey, session),
  })
}

export interface RegisterInput {
  name: string
  email: string
  password: string
  timezone?: string
  /** The unticked "Email me product news and SEO tips" box: consent for marketing email. */
  marketing_opt_in?: boolean
  /** On an agency's portal address: the invitation the login is created from. */
  invitation_token?: string
  /** Whether the visitor allows ad measurement: the API then reports the sign-up to Meta too. */
  ad_consent?: boolean
  /** The browser's event ID for that report, shared with the API's so Meta counts the sign-up once. */
  ad_event_id?: string
  /** The campaign the visit came through (lib/campaign): its tags and the ad platform of the click. */
  signup_source?: SignUpSource
  /** Cloudflare Turnstile's token, when the sign-up form shows it. */
  turnstile_token?: string
}

export const useRegister = () => useSessionMutation<RegisterInput>('/auth/register')

/** What the sign-up form needs: Cloudflare Turnstile's site key, or null when it's off. */
export function useSignupConfig() {
  return useQuery({
    queryKey: ['auth', 'signup-config'],
    queryFn: async () =>
      (await http.get<Resource<{ turnstile_site_key: string | null }>>('/auth/signup-config')).data,
    staleTime: Infinity,
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => http.post('/auth/logout'),
    onSettled: () => {
      // Signed out first (<RequireAuth> moves to sign-in), then everything else is forgotten.
      queryClient.setQueryData(sessionKey, null)
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== sessionKey[0] })
    },
  })
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => http.post<{ message: string }>('/auth/forgot-password', { email }),
  })
}

export interface ResetPasswordInput {
  token: string
  email: string
  password: string
  password_confirmation: string
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (input: ResetPasswordInput) => http.post<{ message: string }>('/auth/reset-password', input),
  })
}

export function useResendVerification() {
  return useMutation({
    mutationFn: () => http.post<{ message: string }>('/auth/email/verification-notification'),
  })
}

export function useUpdateProfile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { name?: string; timezone?: string }) =>
      (await http.patch<Resource<Session>>('/me', input)).data,
    onSuccess: (session) => {
      queryClient.setQueryData(sessionKey, session)
    },
  })
}

export function useUpdatePassword() {
  return useMutation({
    mutationFn: (input: { current_password: string; password: string; password_confirmation: string }) =>
      http.put('/me/password', input),
  })
}

export function useInvitationPreview(token: string | null) {
  return useQuery({
    queryKey: ['invitation-preview', token],
    queryFn: async () =>
      (await http.post<Resource<InvitationPreview>>('/invitations/lookup', { token })).data,
    enabled: Boolean(token),
    staleTime: Infinity,
  })
}

export function useAcceptInvitation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (token: string) =>
      (await http.post<Resource<Workspace>>('/invitations/accept', { token })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: sessionKey }),
  })
}

/** A team invitation waiting for the signed-in person's email (they signed up another way). */
export interface MyInvitation {
  scoped?: boolean
  projects?: import('@/lib/api/types').InvitationProject[]
  requires_explicit_acceptance?: boolean
  id: string
  workspace: { id: string; name: string }
  invited_by: string | null
  role: string
  role_label: string
  expires_at: string
}

export function useMyInvitations(enabled: boolean) {
  return useQuery({
    queryKey: ['me', 'invitations'],
    queryFn: async () => (await http.get<{ data: MyInvitation[] }>('/me/invitations')).data,
    enabled,
  })
}

export function useAcceptMyInvitation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) =>
      (await http.post<Resource<Workspace>>(`/me/invitations/${id}/accept`)).data,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: sessionKey }),
        queryClient.invalidateQueries({ queryKey: ['me', 'invitations'] }),
      ]),
  })
}
